import { computed } from 'vue'
import { useTableService } from '@/global/composables/table/useTableService'
import CellIcon from '@/global/components/table/components/TableCellIcon.vue'
import TruncatedTextDisplay from '@/global/components/TruncatedTextDisplay.vue'
import { dateService, FORMATS } from '@/global/services/date'
import { useWorkOrderReport } from '@/modules/road-maintenance-module/winter-service-v2/reports/composables/useWorkOrderReport'
import { BASES_AUTOCOMPLETE_ENDPOINT } from '@/modules/road-maintenance-module/winter-service-v2/bases/config/constants'
import {
  WINTER_SERVICE_MODULE,
  WINTER_SERVICE_VEHICLES_AUTOCOMPLETE
} from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import {
  CREATE_NOW_DISABLED_ICON,
  CREATE_NOW_ICON,
  REPORTS_ENDPOINT,
  REPORTS_TABLE_KEY,
  REPORTS_TRANSLATION_NAMESPACE as NS,
  REPORTS_UI_VIEW
} from '@/modules/road-maintenance-module/winter-service-v2/reports/config/constants'

const { buildConfig } = useTableService()

const API_CONFIG = {
  get: {
    module: WINTER_SERVICE_MODULE,
    route: REPORTS_ENDPOINT
  }
}

const COLUMN_WIDTH = '25%'

// How much of a text column is shown before it is cut and the rest moves into the
// tooltip. Counted in characters, which is what TruncatedTextDisplay measures - the
// share of the width above is what it was chosen against.
const MAX_TEXT_LENGTH = 40

const TEXT_CELL = {
  style: {
    width: COLUMN_WIDTH
  }
}

/**
 * The winter service report screen, shaped like the reports overview of the base
 * module: a category, the report's name and the column that creates it.
 *
 * The winter service has one report - the coverage report - and it is created per work
 * order, so the rows are the orders and the category is the same on every one of them.
 * That also leaves a single creation column: there is nothing to send by email and
 * nothing to schedule, the report is rendered and downloaded on the spot.
 */
export function useTable (options) {
  const { trans } = options || {}

  const { isCreating, downloadReport } = useWorkOrderReport()

  // Both the cell and the row action are reached with either shape, depending on
  // whether the table hands over the row or the cell event.
  const rowOf = (event) => event?.rowData || event?.data || event

  const isCreateDisabled = (event) => {
    const row = rowOf(event)
    return !row?.report_available || isCreating.value
  }

  const onCreateNow = (event) => downloadReport(rowOf(event))

  const formatTimestamp = (value) => value
    ? dateService.formatCustom(value, FORMATS.isoDatetime, FORMATS.datetimeShort)
    : ''

  /**
   * What the report says, which is the same thing on every row - the coverage of the
   * order's route, street by street. Only the task it was driven for changes, and an
   * order whose plan location no longer resolves to one is described without it rather
   * than with an empty name.
   */
  const toReportDescription = (row) => {
    const taskName = row?.task?.name

    return taskName
      ? trans(`${NS}.report_description`).replace(':task', taskName)
      : trans(`${NS}.report_description_no_task`)
  }

  /**
   * What the row is a report of. There is one report name to give, so it is the order
   * itself that has to be recognisable in it - its number, the vehicle that drove it
   * and when it was taken over.
   */
  const toReportName = (row) => {
    const parts = [
      trans(`${NS}.work_order_report_name`).replace(':id', row?.id ?? ''),
      row?.vehicle?.registration,
      formatTimestamp(row?.started_at || row?.planned_start)
    ]

    return parts.filter(Boolean).join(' · ')
  }

  const cellsConfig = computed(() => ({
    category: {
      ...TEXT_CELL,
      // The screen has a single category, so it is said in full rather than looked up
      // per row - the backend has no category to report.
      custom_display_value: () => trans(`${NS}.category_winter_service`)
    },
    name: {
      ...TEXT_CELL,
      custom_display_value: (data) => toReportName(data)
    },
    description: {
      ...TEXT_CELL,
      custom_display_value: (data) => toReportDescription(data)
    },
    create_now: {
      style: {
        width: COLUMN_WIDTH
      },
      wrapperStyle: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'inherit'
      }
    }
  }))

  const headers = computed(() => ([
    {
      label: trans(`${NS}.category`),
      key: 'category',
      style: {
        width: COLUMN_WIDTH
      },
      component: TruncatedTextDisplay,
      maxTextLength: MAX_TEXT_LENGTH,
      always_visible: true
    },
    {
      label: trans(`${NS}.name`),
      key: 'name',
      style: {
        width: COLUMN_WIDTH
      },
      component: TruncatedTextDisplay,
      maxTextLength: MAX_TEXT_LENGTH,
      always_visible: true
    },
    {
      label: trans(`${NS}.description`),
      key: 'description',
      style: {
        width: COLUMN_WIDTH
      },
      component: TruncatedTextDisplay,
      maxTextLength: MAX_TEXT_LENGTH,
      always_visible: true
    },
    {
      label: trans(`${NS}.create_now`),
      key: 'create_now',
      style: {
        textAlign: 'center',
        width: COLUMN_WIDTH
      },
      component: CellIcon,
      // The cell is the button - the table's own row click must not fire alongside it.
      disableDefaultClick: true,
      clickableCell: true,
      src: (event) => isCreateDisabled(event) ? CREATE_NOW_DISABLED_ICON : CREATE_NOW_ICON,
      disabled: (event) => isCreateDisabled(event),
      onClick: (event) => onCreateNow(event),
      always_visible: true
    }
  ]))

  // Only what the listing endpoint understands. It matches on ids and plain dates
  // rather than text, so each of these is a picker or a date.
  const filtersConfig = computed(() => ({
    fields: [
      {
        key: 'vehicle_id',
        label: trans(`${NS}.vehicle`),
        type: 'autocomplete',
        autocomplete_options: {
          ...WINTER_SERVICE_VEHICLES_AUTOCOMPLETE
        }
      },
      {
        key: 'driver_id',
        label: trans(`${NS}.driver`),
        type: 'autocomplete',
        autocomplete_options: {
          module: 'people',
          route: 'people/autocomplete'
        }
      },
      {
        key: 'base_id',
        label: trans(`${NS}.base`),
        type: 'autocomplete',
        autocomplete_options: {
          module: WINTER_SERVICE_MODULE,
          route: BASES_AUTOCOMPLETE_ENDPOINT
        }
      },
      {
        // The endpoint reads these against the moment the order was taken over, so no
        // time part is sent.
        key: 'from',
        label: trans(`${NS}.date_from`),
        type: 'datetime-picker',
        subType: 'date',
        appendIcon: 'mdi-calendar-month',
        placeholder: FORMATS.date
      },
      {
        key: 'to',
        label: trans(`${NS}.date_to`),
        type: 'datetime-picker',
        subType: 'date',
        appendIcon: 'mdi-calendar-month',
        placeholder: FORMATS.date
      }
    ]
  }))

  const breadcrumbs = computed(() => ({
    items: [
      {
        text: trans('road-maintenance/winter_service2.report'),
        bolded: true
      }
    ]
  }))

  const config = computed(() => ({
    tableConfig: buildConfig({
      key: REPORTS_TABLE_KEY,
      uiView: REPORTS_UI_VIEW,
      apiConfig: API_CONFIG,
      // Rendering the PDF takes as long as it takes, and the icon it was pressed on is
      // one cell in a long row - the table's own loader is what shows the screen is
      // busy with it.
      showLoader: isCreating.value,
      search_and_buttons_config: {
        // Free text over what identifies an order in the report name - its number, the
        // vehicle and the driver.
        search_field_config: {
          placeholder: trans('base.tracker_search'),
          prependInnerIcon: 'mdi-magnify'
        },
        showNewBtn: false
      },
      tableConfig: {
        cellsConfig: cellsConfig.value,
        headers: headers.value,
        filtersConfig: filtersConfig.value
      }
    }),
    breadcrumbs: breadcrumbs.value
  }))

  return {
    config
  }
}
